/**
 * Send an email from a record — `POST /email/send`, real since §8 shipped,
 * but never wired to any screen until this pass (every detail/list page
 * still offered an "Email" button that only opened a "not built" notice, or
 * — leads — a bare `mailto:` link with a comment explaining a composer
 * would have opened onto nothing).
 *
 * Two shapes, one component:
 *
 *  - **Single-anchor mode** (`anchor` prop): one fixed `crm.activity` link
 *    this send attaches to (`{ account_id }`, `{ lead_id }`, `{ contact_id }`,
 *    ...; see `resolveActivityAnchor` in `emailSend.service.js` for what
 *    counts), with an editable "to" address — `defaultEmail` prefills it for
 *    a record that has one obvious recipient (a lead, a contact), and
 *    `contacts` offers a picker when a record has several (an account).
 *  - **Bulk mode** (`bulkRecipients` prop): a fixed list picked from a list
 *    screen (e.g. selected leads), each with its *own* anchor — there is no
 *    single shared `crm.activity` to link to, so this sends once per
 *    recipient with `context: { [bulkContextKey]: recipient.id }` and
 *    reports one combined result rather than routing every call through
 *    `useSendEmail`'s per-call toast.
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Field, ButtonSpinner } from '../../components/common/index.jsx';
import { useTemplates, usePreviewEmail, useSendEmail } from './useEmailTemplates.js';
import { sendEmail as sendEmailRequest } from '../../api/emailTemplates.api.js';
import { pushToast } from '../ui/uiSlice.js';

const CUSTOM = '__custom__';

export function EmailComposerModal({
  open, onClose, anchor, contacts = [], defaultEmail = '', bulkRecipients = null, bulkContextKey = 'lead_id',
}) {
  const dispatch = useDispatch();
  const isBulk = Array.isArray(bulkRecipients);

  const [contactId, setContactId] = useState('');
  const [toEmail, setToEmail] = useState(defaultEmail);
  const [templateId, setTemplateId] = useState(CUSTOM);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  const { data: templatesPage } = useTemplates({ limit: 100 });
  const templates = templatesPage?.data ?? [];
  const template = templates.find((t) => t.id === templateId);

  const preview = usePreviewEmail();
  const send = useSendEmail();

  useEffect(() => {
    if (!open) return;
    setContactId(''); setToEmail(defaultEmail); setTemplateId(CUSTOM); setSubject(''); setBodyHtml('');
    preview.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickContact = (id) => {
    setContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (c?.email) setToEmail(c.email);
  };

  // POST /email/preview requires context.lead_id or context.contact_id — it
  // has no bare "resolve against nothing" mode — so without a contact picked
  // there is nothing to ask it to resolve; the raw template text is shown
  // instead (see the render below). Re-preview whenever either changes, so
  // the send button's own preview never goes stale relative to what will
  // actually be sent. Bulk mode never previews — each recipient resolves
  // differently, and there is no single "the" preview to show.
  useEffect(() => {
    if (isBulk || templateId === CUSTOM || !template || !contactId) return;
    preview.mutate({ template_id: template.id, context: { contact_id: contactId } });
  }, [templateId, contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const usingTemplate = templateId !== CUSTOM;
  const previewData = preview.data;
  const contentReady = usingTemplate ? Boolean(template) : Boolean(subject.trim() && bodyHtml.trim());
  const busy = isBulk ? bulkSending : send.isPending;
  const canSend = Boolean(
    (isBulk ? bulkRecipients.length > 0 : toEmail.trim())
    && contentReady
    && !busy,
  );

  const onSend = async () => {
    if (isBulk) {
      setBulkSending(true);
      const results = await Promise.allSettled(bulkRecipients.map((r) => sendEmailRequest({
        to: [r.email],
        template_id: usingTemplate ? templateId : undefined,
        subject: usingTemplate ? undefined : subject.trim(),
        body_html: usingTemplate ? undefined : bodyHtml,
        context: { [bulkContextKey]: r.id },
      })));
      setBulkSending(false);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === 0) {
        dispatch(pushToast({ tone: 'success', message: `Queued ${results.length} email${results.length === 1 ? '' : 's'} to send.` }));
        onClose();
      } else {
        dispatch(pushToast({
          tone: failed.length === results.length ? 'danger' : 'warning',
          message: `Sent ${results.length - failed.length} of ${results.length}`,
          detail: failed[0].reason?.message,
        }));
        if (failed.length < results.length) onClose();
      }
      return;
    }

    send.mutate(
      {
        to: [toEmail.trim()],
        template_id: usingTemplate ? templateId : undefined,
        subject: usingTemplate ? undefined : subject.trim(),
        body_html: usingTemplate ? undefined : bodyHtml,
        context: contactId ? { ...anchor, contact_id: contactId } : { ...anchor },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Send an email" size="lg"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" disabled={!canSend} onClick={onSend}>
            {busy ? <><ButtonSpinner className="me-1" />Sending…</> : 'Send'}
          </button>
        </>
      )}
    >
      {isBulk ? (
        <Field label="Sending to">
          <div className="p-2 t-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
            {bulkRecipients.length} recipient{bulkRecipients.length === 1 ? '' : 's'}: {bulkRecipients.map((r) => r.label).join(', ')}
          </div>
        </Field>
      ) : (
        <>
          {contacts.length > 0 && (
            <Field label="Send to" help="Pick a contact to personalise the message, or send to any address below.">
              <select className="form-select" value={contactId} onChange={(e) => pickContact(e.target.value)}>
                <option value="">— Choose a contact —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}{c.email ? ` <${c.email}>` : ''}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Email address" required>
            <input
              type="email" className="form-control" placeholder="name@company.com"
              value={toEmail} onChange={(e) => { setToEmail(e.target.value); setContactId(''); }}
            />
          </Field>
        </>
      )}

      <Field label="Template">
        <select className="form-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value={CUSTOM}>Write my own</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>

      {usingTemplate ? (
        <div className="p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
          {!template ? (
            <div className="t-sm ink-3">Choose a template to preview it here.</div>
          ) : !isBulk && contactId && preview.isPending ? (
            <div className="t-sm ink-3"><ButtonSpinner className="me-2" />Resolving merge fields…</div>
          ) : !isBulk && contactId && previewData ? (
            <>
              <div className="t-2xs ink-4 mb-1">Subject</div>
              <div className="t-sm fw-650 mb-3">{previewData.subject}</div>
              <div className="t-2xs ink-4 mb-1">Preview</div>
              {/* Template bodies are authored inside the product (§8's editor), the same
                  trust boundary crm.email_template's own send path already relies on —
                  not arbitrary user input reaching dangerouslySetInnerHTML here. */}
              <div className="t-sm" dangerouslySetInnerHTML={{ __html: previewData.body_html }} />
              {previewData.unresolved_tokens?.length > 0 && (
                <div className="t-2xs mt-2" style={{ color: 'var(--amber)' }}>
                  <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                  Could not resolve: {previewData.unresolved_tokens.join(', ')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="t-2xs ink-4 mb-1">Subject</div>
              <div className="t-sm fw-650 mb-3">{template.subject}</div>
              <div className="t-2xs ink-4 mb-1">Preview</div>
              <div className="t-sm" dangerouslySetInnerHTML={{ __html: template.body_html }} />
              <div className="t-2xs mt-2 ink-4">
                <i className="bi bi-info-circle me-1" aria-hidden="true" />
                {isBulk
                  ? "Merge fields resolve per recipient once sent — each recipient's own lead details fill in."
                  : contacts.length > 0
                    ? 'Merge fields shown as-is — pick a contact above to resolve them.'
                    : 'Merge fields resolve once sent.'}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <Field label="Subject" required>
            <input type="text" className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Message" required>
            <textarea className="form-control" rows={8} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} />
          </Field>
        </>
      )}

      <div className="t-2xs ink-4 mt-3">
        <i className="bi bi-info-circle me-1" aria-hidden="true" />
        Sending only queues the message — delivery happens through your organisation's configured email provider within a few seconds.
      </div>
    </Modal>
  );
}
